import log from 'loglevel';

//eslint-disable-next-line no-restricted-imports
import type DxfArrayScanner from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { serializeGroupValue } from '../../DxfArrayScanner';
//eslint-disable-next-line no-restricted-imports
import { groupIs, getAcadColor, logUnhandledGroup } from '../../ParseHelpers';
//eslint-disable-next-line no-restricted-imports
import { DXFSymbol } from '../../constants';
import {
  parseApplicationGroup,
  serializeApplicationDefinedGroups,
} from '../tables';
import type { ApplicationDefinedGroups } from '../tables';

export type ILayer = {
  applicationGroups?: ApplicationDefinedGroups;
  color?: number; //420
  colorIndex?: number; //62
  frozen?: boolean;
  handle?: string; //5
  lineType?: string; //6
  lineWeight?: number; //370
  materialId?: number; //347
  name: string; //2
  ownerHandle?: string; //330
  plotStyleId?: number; //390
  plottingFlag?: number;
  standardFlags?: number;
  visible?: boolean; //associated with 62
  visualStyle?: string;

  //Note: subMarkers insertion order must be maintained when serializing
  //subMarkers are necessary for eDrawings to import dxf we serialize
  //layers use 1 or 2 subMarker types. 3rd and 4th are added for insurance
  subMarkerCount?: number;
  subMarker1?: string;
  subMarker2?: string;
  subMarker3?: string;
  subMarker4?: string;
};

const layerPropertyFromCode = new Map<number, keyof ILayer>([
  [2, 'name'],
  [5, 'handle'],
  [6, 'lineType'],
  [62, 'colorIndex'],
  [330, 'ownerHandle'],
  [347, 'materialId'],
  [370, 'lineWeight'],
  [390, 'plotStyleId'],
  [348, 'visualStyle'],
]);

const codeFromLayerProperty = new Map<keyof ILayer, number>(
  Array.from(layerPropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);

//delete special cases that are are handled differently when serializing and parsing
layerPropertyFromCode.delete(2);
layerPropertyFromCode.delete(62);
layerPropertyFromCode.delete(70);

codeFromLayerProperty.set('subMarker1', 100);
codeFromLayerProperty.set('subMarker2', 100);
codeFromLayerProperty.set('subMarker3', 100);
codeFromLayerProperty.set('subMarker4', 100);

export const parseLayers = (
  scanner: DxfArrayScanner,
): Record<string, ILayer> => {
  const layers: Record<string, ILayer> = {};
  let layer = {} as ILayer;
  let layerName: string | undefined;

  log.debug('Layer {');
  let curr = scanner.next();
  while (!groupIs(curr, 0, DXFSymbol.EndTable)) {
    const property = layerPropertyFromCode.get(curr.code);
    if (property != null) {
      (layer[property] as string | number) = curr.value as string | number;
      curr = scanner.next();
    } else {
      switch (curr.code) {
        case 2: // layer name
          layerName = layer.name = curr.value as string;
          curr = scanner.next();
          break;
        case 62: // color, visibility
          layer.visible = (curr.value as number) >= 0;
          // TODO 0 and 256 are BYBLOCK and BYLAYER respectively. Need to handle these values for layers?.
          layer.colorIndex = Math.abs(curr.value as number);
          layer.color = getAcadColor(layer.colorIndex);
          curr = scanner.next();
          break;
        case 70: // frozen layer
          layer.standardFlags = curr.value as number;
          layer.frozen =
            ((curr.value as number) & 1) != 0 ||
            ((curr.value as number) & 2) != 0;
          curr = scanner.next();
          break;
        case 100:
          if (!layer.subMarkerCount) {
            layer.subMarkerCount = 1;
          } else {
            layer.subMarkerCount++;
          }
          layer[`subMarker${layer.subMarkerCount}` as 'subMarker1'] =
            curr.value as string;
          curr = scanner.next();
          break;
        case 102:
          if (
            typeof curr.value === 'string' &&
            curr.value.startsWith('{') &&
            curr.value.length > 0
          ) {
            const name = curr.value.substring(1);
            if (!layer.applicationGroups) layer.applicationGroups = {};
            layer.applicationGroups[name] = parseApplicationGroup(scanner);
          }
          curr = scanner.next();
          break;
        case 290: // TrueColor
          layer.plottingFlag = curr.value as number;
          curr = scanner.next();
          break;
        case 420: // TrueColor
          layer.color = Math.abs(curr.value as number);
          curr = scanner.next();
          break;
        case 0:
          // New Layer
          if (curr.value === DXFSymbol.Layer) {
            log.debug('}');
            if (typeof layerName === 'string') {
              layers[layerName] = layer; //save last layer
            }
            log.debug('Layer {');
            layer = {} as ILayer;
            layerName = undefined;
            curr = scanner.next();
          }
          break;
        default:
          logUnhandledGroup(curr);
          curr = scanner.next();
      }
    }
  }
  // Note: do not call scanner.next() here,
  //  parseLayerTable() needs the current group
  log.debug('}');

  if (typeof layerName === 'string') {
    layers[layerName] = layer; //last last layer
  }

  return layers;
};

export const serializeLayers = function* (
  layers: Record<string, ILayer>,
): IterableIterator<string> {
  for (const layer of Object.values(layers) as ILayer[]) {
    yield '0';
    yield DXFSymbol.Layer;

    for (const [property, value] of Object.entries(layer) as [
      keyof ILayer,
      ILayer[keyof ILayer],
    ][]) {
      const code = codeFromLayerProperty.get(property);
      if (code != null) {
        yield `${code}`;
        yield `${value}`;
      } else {
        switch (property) {
          case 'color':
            if (typeof layer.colorIndex !== 'number') {
              //true color (if not colorIndex, but there is a color)
              yield '420';
              yield `${value}`;
            }
            break;
          case 'standardFlags':
            yield '70';
            yield `${value}`;
            break;
          case 'plottingFlag':
            //true color (if not colorIndex, but there is a color)
            yield* serializeGroupValue(290, value as boolean);
            break;
          case 'applicationGroups':
            yield* serializeApplicationDefinedGroups(
              value as ApplicationDefinedGroups,
            );
            break;
        }
      }
    }
  }
};
